<script>
   /*************************************************************/
   /* Root component for the application                        */
   /*************************************************************/

   import { date2str } from '@mdatools-apps/shared/utils';
   import { checkPrepField, checkPrepChain } from '@mdatools-apps/shared/methods/prep.js';

   import TrainAppContainer from './tab_train/AppContainer.svelte';
   import TargetAppContainer from './tab_test/AppContainer.svelte';
   import AltAppContainer from './tab_new/AppContainer.svelte';
   import App from '@mdatools-apps/shared/components/MainApp.svelte';
   import WASM from '@mdatools-apps/wasm/pca_wasm.js';

   /* callback to check validity of JSON with session */
   function checkSession(s) {

      const keys1 = Object.keys(s);
      if (!['date', 'tab', 'train', 'test', 'new'].every(v => keys1.includes(v))) return false;

      const keys2 = Object.keys(s.train);
      if (!['model', 'simca', 'calset', 'calsetp', 'className', 'pvset', 'calres', 'pvres', 'outliers', 'prepType',
         'prepmodel', 'prepmeta', 'prepobj'].every(v => keys2.includes(v))) return false;

      const keys3 = Object.keys(s.test);
      if (!['model', 'simca', 'testset', 'testres', 'outliers', 'ind'].every(v => keys3.includes(v))) return false;

      const keys4 = Object.keys(s.new);
      if (!['model', 'simca', 'newset', 'newres', 'outliers'].every(v => keys4.includes(v))) return false;

      // a stored preprocessing model is rendered as soon as its tab mounts, and the chain read by
      // the creator, where no catch reaches: the checks a file passes (prep's 'checkSession' runs
      // the same); 0 and null are none
      try {
         checkPrepChain(s.train.prepobj);
         for (const p of [s.train.prepmodel, s.test.model?.prep,
                          s.new.model?.prep]) checkPrepField(p);
      } catch { return false; }

      return true;
   }

   /* callback to initialize new session */
   function initSession() {
      return {
         date: date2str(new Date()),
         tab: 0,
         train: {
            model : 0,
            simca: 0,
            calset: 0,
            calsetp: 0,
            className: null,
            pvset: 0,
            calres: {pca: 0, simca: 0},
            pvres: {pca: 0, simca: 0},
            outliers: [],
            prepType: null,
            prepmodel: 0,
            prepmeta: 0,
            prepobj: [],
         },
         test: {
            model : 0,
            simca: 0,
            testset: 0,
            testres: {pca: 0, simca: 0},
            outliers: [],
            ind: null,
         },
         new: {
            model : 0,
            simca: 0,
            newset: 0,
            newres: {pca: 0, simca: 0},
            outliers: []
         }
      }
   }

   // app containers
   const tabs = [
      {name: 'Train', label: 'train', component: TrainAppContainer},
      {name: 'Test', label: 'test', component: TargetAppContainer},
      {name: 'Predict', label: 'new', component: AltAppContainer},
   ]
</script>

<App
   appID="ddsimca"
   appTitle="Data Driven SIMCA"
   appShortTitle="DD-SIMCA"
   appSlogan="One class classifier"
   {WASM}
   {tabs}
   {initSession}
   {checkSession}
>

   {#snippet footer()}
      <p>Current release: <span>2.5.1</span>
      &nbsp;Feedback: <a class="paper-link" href="mailto:help@mda.tools?subject=DDSIMCA app">help@mda.tools</a>
      &nbsp;Learn: <a class="paper-link" href="https://doi.org/10.1002/cem.3556">tutorial</a>
      | <a class="paper-link" href="/ddsimca/Oregano.zip" download>dataset</a>
      | <a href="https://www.youtube.com/playlist?list=PLep7KM9cEyFlcYmmBsyqI6whqUlauVCTz">video lessons</a>
      </p>
   {/snippet}

   {#snippet info()}
   <h2>Data Driven SIMCA</h2>
   <p>Authors: <em>Sergey Kucheryavskiy, Oxana Rodionova, Alexey Pomerantsev</em></p>
   <p>
      Interactive web-application for creating and using DD-SIMCA one class classifier
      directly in your browser. The app does not send any information to a server,
      all calculations are done locally on your device.
   </p>
   <p>Check our open access <a href="https://doi.org/10.1002/cem.3556">tutorial paper</a> and YouTube <a href="https://www.youtube.com/playlist?list=PLep7KM9cEyFlcYmmBsyqI6whqUlauVCTz">video-lessons</a>
   </p>
   <p>
      Related theory:
      <a href="https://doi.org/10.1002/cem.2506">10.1002/cem.2506</a>,
      <a href="https://doi.org/10.1002/cem.2610">10.1002/cem.2610</a>,
      <a href="https://doi.org/10.1002/cem.3250">10.1002/cem.3250</a>,
      <a href="https://doi.org/10.1016/j.aca.2024.343352">10.1016/j.aca.2024.343352</a>.
   </p>
   <h3>v. 2.5.1</h3>
   <p class="release-info">released 01/11/2026</p>
   <ul>
      <li>New higher-contrast theme for the control elements.</li>
      <li>Improved accessibility of the controls.</li>
      <li>Fixed CSV export of PV-set results, where values were shifted relative to the objects.</li>
      <li>Added possibility to save plot data as CSV file.</li>
      <li>New more efficient and faster session system.</li>
      <li>Small bug fixes and improvements.</li>
   </ul>
   {/snippet}
</App>
